import React, { useState, useEffect } from 'react';
import { useStyles } from '../context/StylesContext';

interface SpellCheckResult {
  correctedText: string;
  corrections: Array<{
    original: string;
    corrected: string;
    type: 'spelling' | 'grammar' | 'style';
  }>;
}

export default function SpellCheckPage() {
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [selectedModel, setSelectedModel] = useState('openai/gpt-4.1-nano');
  const [customModelName, setCustomModelName] = useState('');
  const { styles } = useStyles();
  const [selectedStyle, setSelectedStyle] = useState('general');
  const [isProcessing, setIsProcessing] = useState(false);
  const [corrections, setCorrections] = useState<SpellCheckResult['corrections']>([]);
  const [requestTime, setRequestTime] = useState<number | null>(null);

  const modelOptions = [
    { value: 'openai/gpt-4.1-nano', label: 'gpt-4-1-nano' },
    { value: 'openai/gpt-4.1', label: 'gpt-4.1' },
    { value: 'other', label: 'Other (Custom)' }
  ];

  useEffect(() => {
    if (!styles.find((style) => style.value === selectedStyle)) {
      setSelectedStyle(styles[0]?.value ?? 'general');
    }
  }, [styles, selectedStyle]);

  const handleSpellCheck = async () => {
    if (!inputText.trim()) return;

    setIsProcessing(true);
    setRequestTime(null);
    const startTime = Date.now();
    
    try {
      const response = await fetch('/api/spellcheck', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: inputText,
          model: selectedModel === 'other' ? customModelName : selectedModel,
          style: selectedStyle,
        }),
      });

      const endTime = Date.now();
      const duration = endTime - startTime;
      setRequestTime(duration);

      if (response.ok) {
        const result: SpellCheckResult = await response.json();
        setOutputText(result.correctedText);
        setCorrections(result.corrections);
      } else {
        console.error('Failed to process text');
        setOutputText('Error processing text. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      setOutputText('Error processing text. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClearAll = () => {
    setInputText('');
    setOutputText('');
    setCorrections([]);
    setRequestTime(null);
  };

  const handleCopyOutput = () => {
    if (outputText) {
      navigator.clipboard.writeText(outputText);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
       

        {/* Controls */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="model" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                AI Model
              </label>
              <select
                id="model"
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {modelOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {selectedModel === 'other' && (
                <div className="mt-3">
                  <label htmlFor="customModel" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Custom Model Name
                  </label>
                  <input
                    id="customModel"
                    type="text"
                    value={customModelName}
                    onChange={(e) => setCustomModelName(e.target.value)}
                    placeholder="Enter your model name (e.g., gpt-4o-mini)"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              )}
            </div>
            <div>
              <label htmlFor="style" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Writing Style
              </label>
              <select
                id="style"
                value={selectedStyle}
                onChange={(e) => setSelectedStyle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {styles.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Text Areas */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Original Text</h2>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {inputText.length} characters
              </span>
            </div>
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Paste or type your text here..."
              className="w-full h-80 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-md resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={handleSpellCheck}
                disabled={isProcessing || !inputText.trim() || (selectedModel === 'other' && !customModelName.trim())}
                className="flex-1 bg-blue-600 dark:bg-blue-500 text-white px-6 py-3 rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {isProcessing ? 'Processing...' : 'Check & Improve'}
              </button>
              <button
                onClick={handleClearAll}
                className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
              >
                Clear
              </button>
            </div>
          </div>

          {/* Output Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Corrected Text</h2>
              <div className="flex items-center gap-4">
                {requestTime !== null && (
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {requestTime < 1000 ? `${requestTime}ms` : `${(requestTime / 1000).toFixed(1)}s`}
                  </span>
                )}
                {outputText && (
                  <button
                    onClick={handleCopyOutput}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors duration-200"
                  >
                    Copy to clipboard
                  </button>
                )}
              </div>
            </div>
            <textarea
              value={outputText}
              readOnly
              placeholder="Corrected text will appear here..."
              className="w-full h-80 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-md resize-none bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none"
            />
            
            {/* Corrections Summary */}
            {corrections.length > 0 && (
              <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-md">
                <h3 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">
                  Corrections Made ({corrections.length})
                </h3>
                <div className="space-y-1">
                  {corrections.slice(0, 5).map((correction, index) => (
                    <div key={index} className="text-sm text-blue-800 dark:text-blue-200">
                      <span className="line-through">{correction.original}</span>
                      {' → '}
                      <span className="font-medium">{correction.corrected}</span>
                      <span className="text-blue-600 dark:text-blue-400 ml-2 text-xs">
                        ({correction.type})
                      </span>
                    </div>
                  ))}
                  {corrections.length > 5 && (
                    <div className="text-xs text-blue-600 dark:text-blue-400">
                      +{corrections.length - 5} more corrections
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}